import { type ReactNode, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import { Label } from '@/design_system/Label/Label';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  isRequiredPaidEstimateAdvanceDeposit,
  useDepositOptions,
  useUpdateDepositChoice,
} from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

type DepositOptions = Endpoints['GET /requests/:id/deposit-options']['response'];

export const DepositSelector = () => {
  const { request, errors } = useRequestContext();
  const { data: depositOptions } = useDepositOptions({ requestId: request.id });

  if (!depositOptions) {
    return null;
  }

  if (isRequiredPaidEstimateAdvanceDeposit(depositOptions)) {
    return null;
  }

  if (depositOptions.type === 'percentage') {
    return (
      <PercentageDepositSelector
        depositOptions={depositOptions}
        hasError={!!errors.price?.missingDepositChoice}
      />
    );
  }

  return <PaidEstimateAdvanceDepositSelector hasError={!!errors.price?.missingDepositChoice} />;
};

const PercentageDepositSelector = ({
  depositOptions,
  hasError,
}: {
  depositOptions: Extract<DepositOptions, { type: 'percentage' }>;
  hasError: boolean;
}) => {
  const { t } = useLingui();
  const { request } = useRequestContext();
  const deposit = request.deposits[0];

  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
    deposit?.percentage ?? null
  );

  const {
    mutateAsync: updateDepositChoice,
    isPending: isUpdateDepositChoicePending,
    variables,
  } = useUpdateDepositChoice({ requestId: request.id });

  const isLoading =
    isUpdateDepositChoicePending ||
    (deposit?.percentage !== undefined &&
      variables !== undefined &&
      deposit?.percentage !== variables.percentage);

  const { percentageOptions } = depositOptions;
  const showNoneOption = percentageOptions.includes(0);
  const showFullOption = percentageOptions.includes(100);
  const partialPercentage = percentageOptions.find(
    (percentage) => percentage > 0 && percentage < 100
  );

  return (
    <DepositChoiceField hasError={hasError}>
      <RadioBoxGroup
        name="deposit-choice"
        value={selectedPercentage === null ? '' : String(selectedPercentage)}
        onChange={(value) => {
          const percentage = Number(value);

          setSelectedPercentage(percentage);
          updateDepositChoice({ percentage });
        }}
        disabled={isLoading}
        isInvalid={hasError}
      >
        {showNoneOption && (
          <RadioBox
            value="0"
            ariaLabel={t({
              id: 'deposit-payment-choice.radio.none.label',
              message: 'None',
            })}
            title={<Trans id="deposit-payment-choice.radio.none.label">None</Trans>}
          >
            <Trans id="deposit-payment-choice.radio.none.sub-text">
              No charge now. Full amount due later.
            </Trans>
          </RadioBox>
        )}
        {!!partialPercentage && (
          <RadioBox
            value={String(partialPercentage)}
            ariaLabel={t({
              id: 'deposit-payment-choice.radio.partial.label',
              message: 'Partial',
            })}
            title={<Trans id="deposit-payment-choice.radio.partial.label">Partial</Trans>}
          >
            <Trans id="deposit-payment-choice.radio.partial.sub-text">
              Charge part now. The rest later
            </Trans>
          </RadioBox>
        )}
        {showFullOption && (
          <RadioBox
            value="100"
            ariaLabel={t({
              id: 'deposit-payment-choice.radio.full.label',
              message: 'Full',
            })}
            title={<Trans id="deposit-payment-choice.radio.full.label">Full</Trans>}
          >
            <Trans id="deposit-payment-choice.radio.full.sub-text">
              Charge the full amount now.
            </Trans>
          </RadioBox>
        )}
      </RadioBoxGroup>
    </DepositChoiceField>
  );
};

const PaidEstimateAdvanceDepositSelector = ({ hasError }: { hasError: boolean }) => {
  const { t } = useLingui();
  const { request } = useRequestContext();

  const deposit = request.deposits[0];
  const [paidEstimateAdvanceChoice, setPaidEstimateAdvanceChoice] = useState<
    'paid-estimate-advance' | 'none' | null
  >(
    deposit?.type === 'paid-estimate-advance'
      ? deposit.price.amount === 0
        ? 'none'
        : 'paid-estimate-advance'
      : null
  );

  const { mutateAsync: updateDepositChoice, isPending: isUpdateDepositChoicePending } =
    useUpdateDepositChoice({ requestId: request.id });

  return (
    <DepositChoiceField hasError={hasError}>
      <RadioBoxGroup
        name="paid-estimate-advance-choice"
        value={paidEstimateAdvanceChoice}
        onChange={(value) => {
          setPaidEstimateAdvanceChoice(value);

          if (value === 'paid-estimate-advance') {
            updateDepositChoice({ paidEstimateAdvance: true });

            return;
          }

          updateDepositChoice({ paidEstimateAdvance: false });
        }}
        disabled={isUpdateDepositChoicePending}
        isInvalid={hasError}
      >
        <RadioBox
          value="none"
          ariaLabel={t({
            id: 'deposit-payment-choice.radio.none.label',
            message: 'None',
          })}
          title={<Trans id="deposit-payment-choice.radio.none.label">None</Trans>}
        >
          <Trans id="deposit-payment-choice.paid-estimate-advance.none.sub-text">
            No charges now. Full amount due later.
          </Trans>
        </RadioBox>
        <RadioBox
          value="paid-estimate-advance"
          ariaLabel={t({
            id: 'deposit-payment-choice.paid-estimate-advance.label',
            message: 'Paid estimate advance',
          })}
          title={
            <Trans id="deposit-payment-choice.paid-estimate-advance.label">
              Paid estimate advance
            </Trans>
          }
        >
          <Stack gap="0.25rem" row>
            <Trans id="deposit-payment-choice.paid-estimate-advance.sub-text">
              Charge part now, the rest later.
            </Trans>
            <Paragraph color="warning" size="200">
              <Trans id="deposit-payment-choice.paid-estimate-advance.non-refundable">
                Non-refundable if the proposed quote is declined.
              </Trans>
            </Paragraph>
          </Stack>
        </RadioBox>
      </RadioBoxGroup>
    </DepositChoiceField>
  );
};

const DepositChoiceField = ({ children, hasError }: { children: ReactNode; hasError: boolean }) => {
  const { t } = useLingui();

  return (
    <Stack gap="0.5rem">
      <Label
        label={t({
          id: 'deposit-payment-choice.radio.label',
          message: 'Select a deposit amount option',
        })}
      >
        {children}
      </Label>
      {!!hasError && (
        <Paragraph color="danger">
          <Trans id="deposit-payment-choice.error">Please select a deposit amount option</Trans>
        </Paragraph>
      )}
    </Stack>
  );
};
